import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, RefreshCw } from "lucide-react";
import { fmtRange } from "./formatters";
import { placeTimeLabel } from "@/lib/datetime";

const HORIZON_OPTIONS = [
  { value: 24, label: "24h" },
  { value: 48, label: "48h" },
  { value: 72, label: "72h" },
];

const SitePicker = ({ sites, siteId, onChange }) => (
  <div className="flex items-center gap-2 min-w-[220px]">
    <span className="text-xs text-slate-500">Site</span>
    <Select value={siteId} onValueChange={onChange}>
      <SelectTrigger data-testid="dock-site-select" className="w-56">
        <SelectValue placeholder="Pick a site" />
      </SelectTrigger>
      <SelectContent>
        {sites.map((s) => (
          <SelectItem key={s.id} value={s.id}>{s.name} {s.code ? `· ${s.code}` : ""}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const HorizonToggle = ({ horizon, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-slate-500">Horizon</span>
    <div className="flex rounded-md border bg-white p-0.5" data-testid="dock-horizon-toggle">
      {HORIZON_OPTIONS.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
                data-testid={`horizon-${o.value}`}
                className={`px-3 py-1 text-sm rounded transition ${horizon === o.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const NavButtons = ({ onPrev, onNow, onNext, onRefresh }) => (
  <div className="flex items-center gap-1 ml-auto">
    <Button variant="outline" size="sm" onClick={onPrev} data-testid="dock-prev-btn" title="Previous window">
      <ChevronLeft className="h-4 w-4" />
    </Button>
    <Button variant="outline" size="sm" onClick={onNow} data-testid="dock-now-btn">
      <Clock className="h-4 w-4 mr-1.5" /> Now
    </Button>
    <Button variant="outline" size="sm" onClick={onNext} data-testid="dock-next-btn" title="Next window">
      <ChevronRight className="h-4 w-4" />
    </Button>
    <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="dock-refresh-btn" title="Refresh">
      <RefreshCw className="h-4 w-4" />
    </Button>
  </div>
);

export default function DockToolbar({ site, sites, siteId, setSiteId, horizon, setHorizon, nav, windowStart, windowEnd, siteTz, loading }) {
  return (
    <Card>
      <CardContent className="py-3 flex flex-wrap items-center gap-2">
        <SitePicker sites={sites} siteId={siteId} onChange={setSiteId} />
        <HorizonToggle horizon={horizon} onChange={setHorizon} />
        <NavButtons {...nav} />
        <div className="basis-full text-xs text-slate-500 flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5" />
          {fmtRange(windowStart, windowEnd)}
          <span className="text-slate-400" data-testid="dock-timezone-label">· times in {site ? placeTimeLabel(site, windowStart.toISOString()) : siteTz.replace("_", " ")}</span>
          {loading && <span className="text-slate-400">· loading…</span>}
        </div>
      </CardContent>
    </Card>
  );
}
